import clsx from "clsx";
import { Dropdown } from "../Dropdown";
import type { TeamMemberSelectRowProps } from "./types";

interface MemberIdentityProps {
  name: string;
  email: string;
}

const MemberIdentity = ({ name, email }: MemberIdentityProps) => (
  <div className="min-w-0">
    <p className="truncate text-sm text-gray-900">{name}</p>
    {email && email !== name && (
      <p className="truncate text-xs text-gray-500">{email}</p>
    )}
  </div>
);

export const TeamMemberSelectRow = <TRole extends string>({
  name,
  email,
  isSelected,
  role,
  roleOptions,
  onToggle,
  onRoleChange,
  lockedRoleLabel,
}: TeamMemberSelectRowProps<TRole>) => {
  // A locked member always has access and can't be edited (e.g. org admins):
  // a checked, non-interactive row with a static label instead of a dropdown.
  if (lockedRoleLabel) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 pr-3">
        <div className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2">
          <input
            type="checkbox"
            checked
            disabled
            readOnly
            className="h-4 w-4 rounded border-gray-300 text-blue-600"
          />
          <MemberIdentity name={name} email={email} />
        </div>
        <div className="flex w-32 items-center justify-end">
          {lockedRoleLabel}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex items-center justify-between gap-2 rounded-lg border pr-3 transition",
        isSelected
          ? "border-blue-300 bg-blue-50"
          : "border-gray-200 hover:bg-gray-50",
      )}
    >
      <label className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer self-stretch px-3 py-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <MemberIdentity name={name} email={email} />
      </label>
      <Dropdown<TRole>
        options={roleOptions}
        value={role}
        onChange={onRoleChange}
        disabled={!isSelected}
        buttonClassName="w-32 justify-between"
      />
    </div>
  );
};
